import BoldSmallText from '@/components/BoldSmallText';
import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import { dateFormatEnum, timeFormatEnum, timeFormatValue } from '@/constants/enum';
import { handleVisibleBanner, isOpenDateAndTimeFormatInstructionBannerSelector } from '@/redux/slice/banner.slice';
import { handleSaveWidgetSettings, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { BlockStack, InlineGrid, Select } from '@shopify/polaris';
import { format } from 'date-fns/format';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const WidgetDateTimeFormat = () => {
  const { t } = useTranslation('widget_settings');
  const widgetSettings = useSelector(widgetSettingSelector);
  const isOpenDateAndTimeFormatInstructionBanner = useSelector(isOpenDateAndTimeFormatInstructionBannerSelector);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const id = location.state?.scrollToDateFormat;
    if (id) {
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.state]);

  const dateFormatOptions = useMemo(
    () =>
      Object.values(dateFormatEnum).map((option) => ({
        label: t(`customization.dateformat.optionList.${option}`, {
          date: format(new Date(), option),
          format: option,
        }),
        value: option,
      })),
    [t],
  );

  const timeFormatOptions = useMemo(
    () =>
      Object.values(timeFormatEnum).map((option) => ({
        label: t(
          `customization.timeFormat.options.${
            option === 'h:mm aa' ? timeFormatValue.TWELVE_HOURS : timeFormatValue.TWENTY_FOUR_HOURS
          }`,
          {
            time: format(new Date(), option),
            format: option,
          },
        ),
        value: option,
      })),
    [t],
  );

  return (
    <div id="date-time-format-widget-appearance">
      <BlockStack gap={'300'}>
        <BoldSmallText>{t('widgetDesign.general.dateTimeFormat.title')}</BoldSmallText>
        <InformationBanner
          isVisible={isOpenDateAndTimeFormatInstructionBanner}
          onDismiss={() => dispatch(handleVisibleBanner({ key: 'isOpenDateAndTimeFormatInstructionBanner', value: false }))}
        >
          <RegularText>{t('widgetDesign.general.dateTimeFormat.bannerInfo')}</RegularText>
        </InformationBanner>
        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
          <Select
            label={t('widgetDesign.general.dateTimeFormat.dateformat.label')}
            options={dateFormatOptions}
            value={widgetSettings.customizations.dateFormat}
            onChange={(value: dateFormatEnum) => {
              dispatch(
                handleSaveWidgetSettings({
                  customizations: {
                    ...widgetSettings.customizations,
                    dateFormat: value,
                  },
                }),
              );
            }}
          />
          <Select
            label={t('widgetDesign.general.dateTimeFormat.timeFormat.label')}
            options={timeFormatOptions}
            value={widgetSettings.customizations.timeFormat}
            onChange={(value: timeFormatEnum) => {
              dispatch(
                handleSaveWidgetSettings({
                  customizations: {
                    ...widgetSettings.customizations,
                    timeFormat: value,
                  },
                }),
              );
            }}
          />
        </InlineGrid>
      </BlockStack>
    </div>
  );
};
